import dayjs from 'dayjs';
import { isObject } from 'lodash';

import { normalizeExamName } from './normalizeExamName';
import { SURVEILLANCE_EXAM_NAME } from 'FRONTEND/utils/constants';
import { getLabelFromFormId } from 'FRONTEND/utils/getLabelFromFormId';
import i18n from 'FRONTEND/utils/i18n';

const DELETED_EXAM = 'deletedExam';
const NOT_DELETED_EXAM = 'notDeletedExam';


const fieldCache = {};

const filterByDeletedStatus = (value, filterBy) => (
  filterBy === DELETED_EXAM ? Boolean(value.deletedByUser) : !value.deletedByUser
);

const filterDeletedValues = (values, filterBy) => {
  if (!filterBy) {
    return values;
  }

  return values.filter(v => filterByDeletedStatus(v, filterBy));
};

const formatMeasurementValue = (measurement) => ({
  qualifier: measurement.qualifier,
  value: measurement.value,
  unit: measurement.unit,
});

const getValue = ({
  value,
  key,
  medicineList,
  currentForm,
  currentLang,
  filterBy,
}) => {
  // We try to lookup in memory cache or traverse the currentForm to look for the
  // field and see if we have a SelectField with choices
  const currentFieldDefinition = fieldCache[key] || currentForm?.pages.reduce((acc, { fields }) => {
    if (Array.isArray(fields)) {
      let fieldCandidate;
      for (let i = 0; i < fields.length; i++) {
        if (fields[i].id === key) {
          fieldCandidate = fields[i];
        }
        if (fields[i].component === 'Section') {
          fieldCandidate = fields[i].fields.find(({ id }) => id === key);
        }
        // We break from the loop as soon as we have a hit
        if (fieldCandidate) {
          break;
        }
      }

      // avoid false positive with choicesFromApi
      if (fieldCandidate?.choices && ['SelectField', 'RadioField'].includes(fieldCandidate.component)) {
        fieldCache[key] = fieldCandidate;
        acc = fieldCandidate;
      }
    }
    return acc;
  }, null);

  // Array.isArray must come before isObject because lodash isObject returns true for arrays
  if (Array.isArray(value)) {
    if (typeof value[0] === 'object') {
      const filteredValues = filterDeletedValues(value, filterBy);

      if (!filteredValues.length) {
        return '';
      }

      const lastMeasureInInterval = filteredValues[filteredValues.length - 1];

      return formatMeasurementValue(lastMeasureInInterval);
    }

    return value.join(' ');
  }

  if (isObject(value)) {
    if (value.hasOwnProperty('value') && filterBy && !filterByDeletedStatus(value, filterBy)) {
      return '';
    }

    // Handle objects wrapping an array (e.g. CheckboxField stored as { value: ["oui"] })
    if (Array.isArray(value.value)) {
      return getValue({
        value: value.value,
        key,
        medicineList,
        currentForm,
        currentLang,
        filterBy,
      });
    }

    // keeping the label fallback for backward compatibility. We first try to look up the value in the currentFieldDefinition, otherwise in the medicine list
    const option = findOptionFromValue({
      options: currentFieldDefinition?.choices || medicineList || [],
      value,
    });

    // If we don't have an option, we consider it a numerical value not from a Select
    if (!option) {
      return formatMeasurementValue(value);
    }

    return typeof option === 'string' ? option : (option.commercial_name || option.label);
  }

  if (value === 'Yes' || value === 'No') {
    return i18n._(value);
  }

  return value;
};

export const getSurveillanceValuesAsArray = ({
  surveillanceEvent,
  currentForm,
  medicineList,
  surveillanceEventHistory,
  currentLang,
}) => {
  const valuesToConsider = surveillanceEventHistory ? surveillanceEventHistory.values : surveillanceEvent.values;
  const values = valuesToConsider[surveillanceEvent.examName ?? surveillanceEvent.type];

  if (!values) {
    return {
      notDeletedValues: [],
      deletedValues: [],
    };
  }

  // need to sort the values from MEDECINE and MEDICAL_EVOLUTION to have the event/medicine name appear first
  const sortedValues = [SURVEILLANCE_EXAM_NAME.MEDICINE, SURVEILLANCE_EXAM_NAME.MEDICAL_EVOLUTION].includes(normalizeExamName(surveillanceEvent.examName)) ? Object.keys(values).sort() : Object.keys(values);

  const formatValues = filterBy => sortedValues.reduce((acc, currentValueKey) => {
    if (currentValueKey.includes('hours')) {
      return acc;
    }
    const formattedValue = getValue({
      value: values[currentValueKey],
      key: currentValueKey,
      medicineList,
      currentForm,
      filterBy,
    });
    const isMeasurement = isObject(formattedValue);
    const valueDetails = isMeasurement ? formattedValue : { value: formattedValue };
    const hasValue = Boolean(valueDetails.value) || valueDetails.value === 0;

    if (!hasValue) {
      return acc;
    }

    if (currentValueKey === 'documentAdded') {
      acc.push({
        label: i18n._('Document added'),
        value: values[currentValueKey].value,
      });
      return acc;
    }

    const label = getLabelFromFormId(currentForm, currentValueKey);

    if (currentValueKey.includes('date')) {
      acc.push({
        label,
        value: dayjs(valueDetails.value).format('DD-MM-YYYY, HH:mm'),
      });
    } else {
      acc.push({
        label,
        ...valueDetails,
      });
    }

    return acc;
  }, []);

  return {
    notDeletedValues: formatValues(NOT_DELETED_EXAM),
    deletedValues: formatValues(DELETED_EXAM),
  };
};

/**
 * @param {object | Array<Object>} options - The options list from which the info (label, group) are extracted.
 * @param {string | Array<string> | Record<"value", string>} value - The value that was saved in the store.
 * @param {bool} allowCustomOptions - Allow value entered by user.
 * @returns {object | Array<Object>} The choice object that corresponds to the value.
 */
export const findOptionFromValue = ({ options, value, allowCustomOptions, multiple }) => {
  if (!value) {
    return multiple ? [] : null;
  }
  // value can be a number, a string, an array or an object, this ensures we get the raw value in (hopefully)
  // all case
  const rawValue = isObject(value) && !Array.isArray(value) ? value.value : value;
  if (typeof rawValue === 'string' || typeof rawValue === 'number') {
    // options can be a list of string or a list of object options:[{value, label, xxx}]
    const choiceObject = options.find(option => option === rawValue || option.value === rawValue);
    // option from choicesFromApi has props as id, commercial_name, tenant_id, level1...) see DrugReference in drugReferenceApi
    // FIXME: harmonize this to have at least a value/label convention like similar option fields
    const choiceObjectFromApi = options.find(option => option.id === rawValue);
    if (choiceObjectFromApi) {
      return choiceObjectFromApi;
    } else if (choiceObject) {
      return choiceObject;
    } else if (allowCustomOptions) {
      return rawValue;
    }
  } else if (Array.isArray(rawValue)) {
    return rawValue.map(valueItem => {
      const choiceObject = options.find(option => option === valueItem || option.value === valueItem);
      if (choiceObject) {
        return choiceObject;
      } else if (allowCustomOptions) {
        return valueItem;
      } else {
        return null;
      }
    });
  }
};
